export interface RetryWithBackoffOptions {
  attempts?: number
  retryDelayMs?: number
  timeoutMs?: number
}

const delay = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds))

const withTimeout = async <Result>(operation: () => Promise<Result>, timeoutMs: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    return await Promise.race([
      operation(),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("Operation timed out")), timeoutMs)
      }),
    ])
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Runs an asynchronous operation with retry attempts, exponential backoff, and a per-attempt timeout.
 *
 * The operation is attempted up to `attempts` times. After each failed attempt, the next retry is delayed
 * by `retryDelayMs * 2 ** attempt`, where `attempt` starts at `0`.
 *
 * Note: When a timeout occurs the operation isn't aborted and may still complete.  Further you
 *    could end up having multiple of the same operations running concurrently.
 *
 *    eg: When using with import like:
 *    ```
 *    retryWithBackoff(() => import("@nprapps/sidechain"))
 *    ```
 *
 *    If the request to import a packege takes 20s. The timeout will occur after 10s and
 *    then another import will be attempted. However, the browser will not make another request.
 *
 * @typeParam Result - The resolved value type returned by the operation.
 * @param operation - The asynchronous operation to run.
 * @param options - Optional retry, delay, and timeout configuration.
 * @param options.attempts - Maximum number of attempts before rethrowing the last error. Defaults to `3`.
 * @param options.retryDelayMs - Initial retry delay in milliseconds. Defaults to `500`.
 * @param options.timeoutMs - Per-attempt timeout in milliseconds. Defaults to `10_000`.
 * @returns The resolved result of the first successful operation attempt.
 * @throws The last operation error, or a timeout error if an attempt exceeds `timeoutMs`.
 */
export const retryWithBackoff = async <Result>(
  operation: () => Promise<Result>,
  { attempts = 3, retryDelayMs = 500, timeoutMs = 10_000 }: RetryWithBackoffOptions = {},
) => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await withTimeout(operation, timeoutMs)
    } catch (error) {
      if (attempt === attempts - 1) throw error
      await delay(retryDelayMs * 2 ** attempt)
    }
  }

  throw new Error("Operation was not attempted")
}
